"use client";

import { useEffect, useRef, useState } from "react";
import { obtainPowProof } from "@/lib/auth-pow-client";

/**
 * Branded captcha widget. Renders a small "verified by {company}" card and,
 * depending on the provider configured in /admin/seguranca:
 *  - pow            → solves a SHA-256 Proof-of-Work in the background
 *  - recaptcha_v2   → renders the Google checkbox inside the card
 *  - recaptcha_v3   → invisible; executes a token on mount + refresh
 *  - disabled / flow off → renders nothing
 *
 * Writes hidden inputs (`powChallenge`/`powNonce`/`recaptchaToken`) that the
 * server actions already read. Place it INSIDE the <form>.
 */

type Provider = "pow" | "recaptcha_v2" | "recaptcha_v3" | "disabled";
export type CaptchaWidgetFlow =
  | "admin_login"
  | "seller_login"
  | "customer_login"
  | "customer_register"
  | "login"
  | "form";

interface CaptchaConfig {
  enabled: boolean;
  provider: Provider;
  siteKey: string;
  applyTo: Record<string, boolean>;
  brand: { logoUrl: string; title: string; slogan: string; info: string };
}

interface Grecaptcha {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, opts: { action: string }) => Promise<string>;
  render: (
    el: HTMLElement,
    opts: { sitekey: string; callback: (t: string) => void; "expired-callback"?: () => void; theme?: string },
  ) => number;
}

function grecaptcha(): Grecaptcha | undefined {
  return (window as unknown as { grecaptcha?: Grecaptcha }).grecaptcha;
}

function loadRecaptchaScript(siteKey: string, v3: boolean): Promise<void> {
  return new Promise((resolve, reject) => {
    const id = "recaptcha-script";
    if (document.getElementById(id)) return resolve();
    const s = document.createElement("script");
    s.id = id;
    s.src = v3
      ? `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`
      : "https://www.google.com/recaptcha/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("recaptcha_script_failed"));
    document.head.appendChild(s);
  });
}

const ShieldCheck = ({ color }: { color: string }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

export function CaptchaWidget({
  flow,
  variant = "light",
  onReady,
}: {
  flow: CaptchaWidgetFlow;
  variant?: "light" | "dark";
  /** Called as the captcha's readiness changes — lets the form gate submit. */
  onReady?: (ready: boolean) => void;
}) {
  const [config, setConfig] = useState<CaptchaConfig | null>(null);
  const [status, setStatus] = useState<"loading" | "working" | "verified" | "error">("loading");
  const [pow, setPow] = useState({ challenge: "", nonce: "" });
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const v2ContainerRef = useRef<HTMLDivElement>(null);
  const v2Rendered = useRef(false);

  // 1. Fetch the captcha config.
  useEffect(() => {
    let cancelled = false;
    fetch("/api/auth/captcha-config", { cache: "no-store" })
      .then((r) => r.json())
      .then((c: CaptchaConfig) => {
        if (!cancelled) setConfig(c);
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // `form` não é um fluxo de auth — quando o widget é usado num formulário
  // público, o captcha é exigido sempre que houver um provedor configurado.
  const active =
    !!config &&
    config.enabled &&
    config.provider !== "disabled" &&
    (flow === "form" || !!config.applyTo[flow]);

  // Tell the parent form when it's safe to submit: captcha not required, or
  // solved, or failed (let the server return a clear message).
  useEffect(() => {
    if (!config) {
      onReady?.(false);
      return;
    }
    onReady?.(!active || status === "verified" || status === "error");
  }, [config, active, status, onReady]);
  const provider = config?.provider;

  // 2. PoW — solve in background, re-solve before the 5 min TTL.
  useEffect(() => {
    if (!active || provider !== "pow") return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    async function solve() {
      setStatus("working");
      try {
        const proof = await obtainPowProof(flow === "admin_login" ? "admin" : "default");
        if (cancelled) return;
        setPow(proof);
        setStatus("verified");
        timer = setTimeout(solve, 4 * 60_000); // refresh before TTL
      } catch {
        if (!cancelled) setStatus("error");
      }
    }
    void solve();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [active, provider, flow]);

  // 3. reCAPTCHA v3 — invisible token, refreshed periodically.
  useEffect(() => {
    if (!active || provider !== "recaptcha_v3" || !config?.siteKey) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    async function run() {
      setStatus("working");
      try {
        await loadRecaptchaScript(config!.siteKey, true);
        const token = await new Promise<string>((resolve, reject) => {
          const g = grecaptcha();
          if (!g) return reject(new Error("recaptcha_unavailable"));
          g.ready(() => {
            g.execute(config!.siteKey, { action: flow }).then(resolve).catch(reject);
          });
        });
        if (cancelled) return;
        setRecaptchaToken(token);
        setStatus("verified");
        timer = setTimeout(run, 90_000); // v3 tokens expire ~2 min
      } catch {
        if (!cancelled) setStatus("error");
      }
    }
    void run();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [active, provider, config, flow]);

  // 4. reCAPTCHA v2 — render the checkbox inside the card.
  useEffect(() => {
    if (!active || provider !== "recaptcha_v2" || !config?.siteKey) return;
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStatus("working");
    loadRecaptchaScript(config.siteKey, false)
      .then(() => {
        if (cancelled || v2Rendered.current || !v2ContainerRef.current) return;
        const tryRender = () => {
          if (cancelled) return;
          const g = grecaptcha();
          if (!g?.render) {
            setTimeout(tryRender, 200);
            return;
          }
          g.render(v2ContainerRef.current!, {
            sitekey: config!.siteKey,
            theme: variant === "dark" ? "dark" : "light",
            callback: (t: string) => {
              setRecaptchaToken(t);
              setStatus("verified");
            },
            "expired-callback": () => {
              setRecaptchaToken("");
              setStatus("working");
            },
          });
          v2Rendered.current = true;
        };
        tryRender();
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [active, provider, config, variant]);

  // Nothing to show — captcha disabled or not applied to this flow.
  if (config && !active) return null;

  const brand = config?.brand;
  const isRecaptcha = provider === "recaptcha_v2" || provider === "recaptcha_v3";

  // Status badge — functional UI label (verifying / verified / failed).
  const statusText =
    status === "loading"
      ? "Carregando…"
      : status === "working"
        ? "Verificando…"
        : status === "verified"
          ? brand?.title || "Verificado"
          : "Falha na verificação";
  const statusColor = status === "verified" ? "#16a34a" : status === "error" ? "#dc2626" : "#a16207";

  return (
    <div className={`captcha-widget captcha-widget--${variant}`}>
      {/* Hidden inputs consumed by the server actions */}
      <input type="hidden" name="powChallenge" value={pow.challenge} />
      <input type="hidden" name="powNonce" value={pow.nonce} />
      <input type="hidden" name="recaptchaToken" value={recaptchaToken} />

      {provider === "recaptcha_v2" ? (
        <div ref={v2ContainerRef} className="captcha-widget__v2" />
      ) : null}

      <div className="captcha-widget__brand">
        <div className="captcha-widget__text">
          <span className="captcha-widget__badge" style={{ color: statusColor }}>
            <ShieldCheck color={statusColor} />
            {statusText}
          </span>
          {isRecaptcha ? (
            <span className="captcha-widget__by">Protegido por reCAPTCHA</span>
          ) : brand?.slogan ? (
            <span className="captcha-widget__by">{brand.slogan}</span>
          ) : null}
        </div>
        {brand?.logoUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={brand.logoUrl} alt="" className="captcha-widget__logo" />
        ) : null}
      </div>

      {!isRecaptcha && brand?.info ? <p className="captcha-widget__policy">{brand.info}</p> : null}

      {isRecaptcha ? (
        <p className="captcha-widget__policy">
          Sujeito à{" "}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Política de Privacidade</a> e aos{" "}
          <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">Termos</a> do Google.
        </p>
      ) : null}
    </div>
  );
}
